import { Link } from 'react-router-dom'
import { FaHeart, FaEnvelope, FaUsers, FaCertificate, FaHome, FaCalendarDay, FaUserCheck, FaPhone } from 'react-icons/fa'
import styles from './Home.module.css'
import useContent from '../hooks/useContent'

const Home = () => {
  const { content, loading, error } = useContent('home')

  if (loading) {
    return (
      <div style={{ padding: '4rem 0', textAlign: 'center' }}>
        <p>Content laden...</p>
      </div>
    )
  }

  if (error) {
    console.error('Content load error:', error)
  }

  // Gebruik content uit JSON indien beschikbaar, anders fallback naar originele tekst
  const pageContent = content || {
    title: "Welkom bij SOBE Zorg Twente",
    heroText: "Bij Sobé Zorg Twente bieden wij professionele dagbesteding en individuele begeleiding voor ouderen. Wij zijn gespecialiseerd in de zorg voor multiculturele cliënten en bieden een warme, inclusieve omgeving waar iedereen zich thuis voelt.",
    features: [
      { title: "Persoonlijke aandacht", description: "Elk individu is uniek. Wij stemmen onze begeleiding af op de persoonlijke wensen en behoeften van onze cliënten.", icon: "FaUsers" },
      { title: "Gekwalificeerd team", description: "Ons team bestaat uit gediplomeerde en ervaren zorgprofessionals die met hart en ziel werken.", icon: "FaCertificate" },
      { title: "Warme sfeer", description: "Een veilige en huiselijke omgeving waar cliënten zich thuis voelen en kunnen ontspannen.", icon: "FaHome" }
    ]
  }

  return (
    <div className={styles.homePage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className="container">
          <div className={styles.heroGrid}>
            <div className="fade-in">
              <h2 className={styles.heroTitle}>
                Zorg met <span className="text-primary">hart</span> voor <span className="text-secondary">ouderen</span>
              </h2>
              <p className={styles.heroText}>
                {pageContent.heroText}
              </p>
              <div className={styles.heroButtons}>
                <Link to="/diensten" className={styles.btnPrimary}>
                  <FaHeart aria-hidden="true" /> Onze diensten
                </Link>
                <Link to="/contact" className={styles.btnSecondary}>
                  <FaEnvelope aria-hidden="true" /> Contact opnemen
                </Link>
              </div>
            </div>
            <div className="fade-in-delay">
              <div className={styles.heroImage}>
                <img 
                  src="/assets/img/dagbesteding-gebouw.jpg" 
                  alt="De dagbesteding locatie van Sobé Zorg Twente in Hengelo" 
                  onError={(e) => {
                    e.target.onerror = null
                    e.target.src = ''
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.featuresSection}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <h2>Waarom kiezen voor SOBE Zorg Twente?</h2>
            <p>Wij bieden meer dan alleen zorg, wij bieden een thuis</p>
          </div>
          
          <div className={styles.featuresGrid}>
            {pageContent.features && pageContent.features.map((feature, index) => {
              const IconComponent = feature.icon === 'FaUsers' ? FaUsers : 
                                   feature.icon === 'FaCertificate' ? FaCertificate : FaHome
              const delayClass = index === 1 ? 'fade-in-delay' : index === 2 ? 'fade-in-delay-2' : 'fade-in'
              const colorClass = index % 2 === 0 ? 'text-primary' : 'text-secondary'
              
              return (
                <div key={index} className={`${styles.featureCard} ${delayClass}`}>
                  <div className={`${styles.featureIcon} ${colorClass}`}>
                    <IconComponent />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className={styles.servicesSection}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <h2>Onze diensten</h2>
            <p>Professionele zorg afgestemd op uw behoeften</p>
          </div>
          
          <div className={styles.servicesGrid}>
            <div className={styles.serviceCard}>
              <div style={{ padding: '2.5rem' }}>
                <div className={`${styles.serviceIcon} text-primary`}>
                  <FaCalendarDay />
                </div>
                <h3>Dagbesteding</h3>
                <p>
                  Een zinvolle en gestructureerde dagbesteding met activiteiten die aansluiten 
                  bij de interesses en mogelijkheden van onze cliënten.
                </p>
                <Link to="/diensten#dagbesteding" className={styles.serviceLink}>
                  Meer informatie <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
            
            <div className={styles.serviceCard}>
              <div style={{ padding: '2.5rem' }}>
                <div className={`${styles.serviceIcon} text-secondary`}>
                  <FaUserCheck />
                </div>
                <h3>Individuele Begeleiding</h3>
                <p>
                  Één-op-één begeleiding die aansluit bij de persoonlijke doelen en wensen. 
                  Thuis of in de samenleving.
                </p>
                <Link to="/diensten#begeleiding" className={styles.serviceLink}>
                  Meer informatie <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className="container">
          <h2>Klaar om kennis te maken?</h2>
          <p>Neem vrijblijvend contact met ons op voor een persoonlijk gesprek</p>
          <div className={styles.ctaButtons}>
            <Link to="/contact" className={styles.ctaBtnWhite}>
              <FaEnvelope aria-hidden="true" /> Stuur een bericht
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
